import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CountrySelect } from "@/components/country-select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useMyReferralCode } from "@/hooks/useReferral";
import { myProfileQuery } from "@/lib/queries";
import { fullProfileQuery } from "@/lib/people";
import { uploadImage } from "@/lib/uploads";
import { ensureHttps } from "@/lib/behot";
import { PayoutMethods } from "@/components/payout-methods";
import { SocialAccountsPanel } from "@/components/social-accounts";
import { CreatorServicesPanel } from "@/components/creator-services";
import { ProProfilePanel } from "@/components/pro-profile-panel";
import { AmbassadorPanel } from "@/components/ambassador-panel";
import { ShowcasePostsPanel } from "@/components/showcase-posts";
import { ShareButtons } from "@/components/share-buttons";
import { privatePageMetadata } from "@/lib/seo";
import { MyPageLink } from "@/components/my-page-link";
import { ImageCropDialog } from "@/components/image-crop-dialog";
import { useT } from "@/i18n";


export const Route = createFileRoute("/app/profile")({
  head: () =>
    privatePageMetadata(
      "/app/profile",
      "Profile | BeHot.Now",
      "Manage your public BeHot.Now profile.",
    ),
  component: ProfilePage,
});

function ProfilePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useT();
  const { data: profile } = useQuery(fullProfileQuery(user?.id));
  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin", user?.id],
    enabled: Boolean(user),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id)
        .eq("role", "admin")
        .maybeSingle();
      if (error) throw error;
      return Boolean(data);
    },
  });
  const myReferralCode = useMyReferralCode();

  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [slug, setSlug] = useState("");
  const [country, setCountry] = useState("");
  const [bio, setBio] = useState("");
  const [headline, setHeadline] = useState("");
  const [niches, setNiches] = useState("");
  const [website, setWebsite] = useState("");
  const [avatar, setAvatar] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [openToWork, setOpenToWork] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setDisplayName(profile.display_name ?? "");
    setHandle(profile.handle ?? "");
    setSlug(profile.slug ?? profile.handle ?? "");
    setCountry(profile.country_code ?? "");
    setBio(profile.bio ?? "");
    setHeadline(profile.headline ?? "");
    setNiches((profile.niches ?? []).join(", "));
    setWebsite(profile.website_url ?? "");
    setAvatar(profile.avatar_url ?? "");
    setIsPublic(profile.is_public);
    setOpenToWork(profile.open_to_work);
  }, [profile]);


  function pickAvatar(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("account.profile.uploadFailed"));
      return;
    }
    setCropFile(file);
    setCropOpen(true);
  }

  async function handleAvatar(file: File | undefined) {
    if (!file || !user) return;
    setUploading(true);
    try {
      const url = await uploadImage(file, user.id, "avatar");
      setAvatar(url);
      toast.success(t("account.profile.avatarUploaded"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("account.profile.uploadFailed"));
    } finally {
      setUploading(false);
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          display_name: displayName.trim() || null,
          handle: handle.trim() || null,
          slug: slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || null,
          country_code: country.trim().toUpperCase().slice(0, 2) || null,
          bio: bio.trim() || null,
          headline: headline.trim() || null,
          niches: niches
            .split(",")
            .map((n) => n.trim())
            .filter(Boolean)
            .slice(0, 10),
          is_public: isPublic,
          open_to_work: openToWork,
          website_url: ensureHttps(website),
          avatar_url: avatar || null,
        })
        .eq("id", user.id);
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["full-profile", user.id] });
      await queryClient.invalidateQueries({ queryKey: ["my-profile", user.id] });
      toast.success(t("account.profile.saved"));

    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "object" && err && "message" in err
            ? String((err as { message: unknown }).message)
            : t("account.profile.saveError");
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-8">
    <MyPageLink />
    <ImageCropDialog
      file={cropFile}
      open={cropOpen}
      onOpenChange={setCropOpen}
      onCropped={(f) => handleAvatar(f)}
    />
    <form onSubmit={save} className="card-surface space-y-5 p-6">
      <div className="flex items-center gap-4">
        {avatar ? (
          <img src={avatar} alt="" className="size-20 rounded-full object-cover" />
        ) : (
          <div className="flex size-20 items-center justify-center rounded-full bg-accent text-2xl font-bold text-accent-foreground">
            {(displayName || user?.email || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <Label htmlFor="avatar" className="cursor-pointer">
            <span className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
              <Upload className="size-4" />{" "}
              {uploading ? t("account.profile.uploading") : t("account.profile.uploadAvatar")}
            </span>
          </Label>
          <input
            id="avatar"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              pickAvatar(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <p className="mt-1 text-xs text-muted-foreground">{t("account.profile.avatarHint")}</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="display">{t("account.profile.displayName")}</Label>
          <Input id="display" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="handle">{t("account.profile.handle")}</Label>
          <Input
            id="handle"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder={t("account.profile.handlePlaceholder")}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="slug">{t("account.profile.slug")}</Label>
        <Input
          id="slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder={t("account.profile.slugPlaceholder")}
        />
        <p className="text-xs text-muted-foreground">
          {t("account.profile.slugHint", { slug: slug || "your-slug" })}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="country">{t("account.profile.country")}</Label>
          <CountrySelect id="country" value={country} onChange={setCountry} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="site">{t("account.profile.website")}</Label>
          <Input
            id="site"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            onBlur={(e) => setWebsite(ensureHttps(e.target.value) ?? "")}
            placeholder="https://"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="headline">{t("account.profile.headline")}</Label>
        <Input
          id="headline"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder={t("account.profile.headlinePlaceholder")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="niches">{t("account.profile.niches")}</Label>
        <Input
          id="niches"
          value={niches}
          onChange={(e) => setNiches(e.target.value)}
          placeholder={t("account.profile.nichesPlaceholder")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="bio">{t("account.profile.bio")}</Label>
        <Textarea id="bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={4} />
      </div>

      <div className="space-y-3 rounded-xl border border-border p-4">
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            className="size-4"
          />
          {t("account.profile.showPublic")}
        </label>
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={openToWork}
            onChange={(e) => setOpenToWork(e.target.checked)}
            className="size-4"
          />
          {t("account.profile.openToWork")}
        </label>
        {(slug || handle) && (
          <div className="space-y-2 text-xs text-muted-foreground">
            <p>
              {t("account.profile.publicPage")}{" "}
              <span className="font-medium">/user/{slug || handle}</span>
              {profile?.pro_enabled && (
                <>
                  {" "}· {t("account.profile.proPage")}{" "}
                  <span className="font-medium">/pro/{slug || handle}</span>
                </>
              )}
            </p>
            <ShareButtons
              path={`/user/${slug || handle}`}
              refCode={myReferralCode}
              title={t("account.page.shareTitle", { name: displayName || handle })}
            />
          </div>
        )}
      </div>

      <div className="space-y-1">
        <Label>{t("account.profile.email")}</Label>
        <p className="text-sm text-muted-foreground">{user?.email}</p>
      </div>

      <Button type="submit" variant="hot" disabled={saving}>
        {saving ? t("account.profile.saving") : t("account.profile.save")}
      </Button>
    </form>

    <AmbassadorPanel
      userId={user?.id}
      isAmbassador={profile?.is_ambassador === true}
      isAdmin={isAdmin === true}
    />
    <SocialAccountsPanel userId={user?.id} />
    <ShowcasePostsPanel userId={user?.id} />
    <CreatorServicesPanel userId={user?.id} />
    <ProProfilePanel userId={user?.id} />
    <PayoutMethods email={user?.email} />
    </div>
  );

}
